import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useAtom, useAtomValue } from 'jotai';
import { Button as ButtonV2, TextInput } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';

import { editIdAtom, editModeAtom, TFormValues } from '.';
import { useNoteMutation } from './use-candidate-notes-mutation';

export const CandidateNotesForm = () => {
  const { t } = useTranslation('common');
  const { trackEvent } = useAnalytics();
  const {
    handleSubmit,
    formState: { errors },
  } = useFormContext<TFormValues>();

  const [isEditMode, setEditMode] = useAtom(editModeAtom);
  const [editId, setEditId] = useAtom(editIdAtom);

  const { addNoteMutation, updateNoteMutation, isPending } = useNoteMutation();
  const addNotes = useCallback(
    async (values: { note: string }) => {
      if (isEditMode && editId !== -1) {
        updateNoteMutation({ noteId: editId, note: values.note });
        setEditMode(false);
        setEditId(-1);
        return;
      }
      trackEvent('employer_add_note', {});
      addNoteMutation({ note: values.note });
    },
    [addNoteMutation, editId, isEditMode, setEditId, setEditMode, trackEvent, updateNoteMutation]
  );
  return (
    <form>
      <div className="fixed left-0 right-0 bottom-0 bg-neutral-10 py-2 px-4 border-t border-neutral-40 lg:absolute lg:py-5 lg:px-10 z-10">
        <div className="relative">
          <InputFieldFormItem />
          {errors.note?.message && <div className="text-red-500 text-xs pl-1 pt-1.5">{errors.note?.message}</div>}
          <ButtonV2
            loading={isPending}
            type="submit"
            size="sm"
            onClick={handleSubmit(addNotes)}
            className={'absolute top-[8px] right-4 min-h-[32px] h-8 lg:min-h-[32px]'}
          >
            {t('add')}
          </ButtonV2>
        </div>
      </div>
    </form>
  );
};
const InputFieldFormItem = ({ isPending }: { isPending?: boolean }) => {
  const { control } = useFormContext<TFormValues>();
  const { t } = useTranslation('common');
  const isEditMode = useAtomValue(editModeAtom);

  return (
    <Controller
      name="note"
      control={control}
      rules={{ required: t('please_enter_your_note') }}
      render={({ field }) => (
        <TextInput
          id="write-new-note"
          placeholder={t('write_a_note_here')}
          type="text"
          disabled={isPending}
          autoFocus
          className="pr-[120px] bg-neutral-10"
          ref={(e) => {
            field.ref(e);
            if (isEditMode && e) {
              e.focus();
            }
          }}
          {...field}
        />
      )}
    />
  );
};
